// ── Making a stored file reachable ─────────────────────────────────────────
//
// Most uploads carry an absolute Cloudinary URL and need nothing done to them.
// Some carry a filesystem path instead, written when the server was storing to
// local disk: /opt/render/project/src/server/uploads/1759-photo.jpg.
//
// A path like that in an <img src> is read as a site URL. The browser asks
// qpshub.com for /opt/render/..., the single-page app's catch-all answers, and
// what renders inside the frame is the admin home screen. Clicking the
// thumbnail navigates there for real — which is what it looked like when a
// photo "took you back to the admin screen".
//
// The server stores proper URLs now (see server/upload.js, fileUrl). This is
// the other half: records written BEFORE that fix still hold the old paths,
// and they are the ones Julian is looking at today. Repairing on read means
// his existing invoices work without a migration, and it costs nothing for a
// URL that was already fine.
export const publicUrl = (stored) => {
  const s = String(stored || '').trim();
  if (!s) return '';
  if (/^(https?:|data:|blob:)/i.test(s)) return s;
  // Anything that went through the uploads folder, however it was written.
  const m = s.match(/(?:^|[\\/])uploads[\\/]([^\\/?#]+)$/);
  if (m) return `/uploads/${m[1]}`;
  // Already root-relative, or something we should not rewrite blind.
  return s.startsWith('/') ? s : `/${s}`;
};

// For a list of them, dropping anything empty.
export const publicUrls = (list = []) => list.map(publicUrl).filter(Boolean);

// ── Making a stored image actually displayable ─────────────────────────────
//
// A URL being correct is not the same as a browser being able to show it.
// Every technician here works off an iPhone, and an iPhone shoots HEIC. The
// upload succeeds, Cloudinary stores it happily, the URL is perfectly valid —
// and Safari aside, no browser can decode it. What renders is a broken image,
// which looks exactly like a file that is missing.
//
// Asking Cloudinary for f_auto hands back whatever the asking browser can
// actually read: HEIC becomes JPEG, and a photo shot on an Android becomes
// WebP. q_auto drops a 6MB camera original to something sane for a thumbnail
// that is 72 pixels wide.
//
// Costs nothing when the file was already a JPEG, and leaves everything that
// is not a Cloudinary image exactly as it was.
export const thumbUrl = (stored) => {
  const u = publicUrl(stored);
  if (!/^https?:\/\/res\.cloudinary\.com\//i.test(u)) return u;
  // Only images. Video gets a play tile and raw files get a document tile,
  // and neither wants an image transformation in front of it.
  const at = u.indexOf('/image/upload/');
  if (at === -1) return u;
  const head = u.slice(0, at + '/image/upload/'.length);
  const tail = u.slice(at + '/image/upload/'.length);
  // Already carries a transformation — leave it alone rather than stack a
  // second one in front of it.
  if (/^[^/]*[,_][^/]*\//.test(tail) && !/^v\d+\//.test(tail)) return u;
  return `${head}f_auto,q_auto/${tail}`;
};
